import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import pilates1 from '../../../../assets/img/gallery/pilates/1.jpeg';
import pilates2 from '../../../../assets/img/gallery/pilates/2.jpeg';
import pilates3 from '../../../../assets/img/gallery/pilates/3.jpeg';
import videoImage from '../../../../assets/img/video-1.jpg';
import { ROUTES } from '../../../routes/paths';
import { Features } from '../../../../assets/features/Features';
import { useLanguage } from '../../../../context/LanguageContext';
import { useTheme } from '../../../../context/ThemeContext';
import SEO from '../../seo/SEO';
import { seoData } from '../../../../data/seoData';


const AboutUsPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialize AOS animation library
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });
  }, []);

  // Handle appointment button click
  const handleAppointmentClick = () => {
    navigate(ROUTES.HOME);
    setTimeout(() => {
      const appointmentSection = document.getElementById('appointment');
      if (appointmentSection) {
        appointmentSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Dark mode styles
  const locationSectionStyle = isDarkMode ? {
    backgroundColor: '#2a2c2e'
  } : { backgroundColor: '#f8f9fa' };

  const cardStyle = isDarkMode ? {
    backgroundColor: '#242526',
    color: '#f3f4f6'
  } : { backgroundColor: '#ffffff' };

  return (
    <>
      <SEO 
        title={seoData.aboutUs.title}
        description={seoData.aboutUs.description}
        keywords={seoData.aboutUs.keywords}
        canonical={seoData.aboutUs.canonical}
      />
      {/* Hero Section */}
      <section 
        className="about-hero d-flex align-items-center" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${videoImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1 
                className="display-4 fw-bold" 
                data-aos="fade-up" 
                style={{ 
                  color: '#ffffff', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {Features.aboutPage.hero.title[language]}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="about-content section py-5">
        <div className="container">
          <div className="row gy-5">
            <div className="col-lg-7" data-aos="fade-right" data-aos-delay="100">
              <div className="content pe-lg-5">
                <h2 className="mb-4">{Features.aboutPage.mainContent.title[language]}</h2>
                
                <p className="mb-4">
                  {Features.aboutPage.mainContent.description1[language]}
                </p>
                
                <p className="mb-4">
                  {Features.aboutPage.mainContent.description2[language]}
                </p>
                
                <p className="mb-4">
                  {Features.aboutPage.mainContent.description3[language]}
                </p>
                
                <p className="mb-4">
                  {Features.aboutPage.mainContent.description4[language]}
                </p>
                
                <blockquote className="blockquote border-start ps-4 border-primary border-3 my-5" data-aos="fade-up" data-aos-delay="300">
                  <p className="fst-italic">
                    {Features.aboutPage.mainContent.quote.text[language]}
                  </p>
                  <footer className="blockquote-footer mt-2">
                    {Features.aboutPage.mainContent.quote.author[language]} <cite title={Features.aboutPage.mainContent.quote.title[language]}>{Features.aboutPage.mainContent.quote.title[language]}</cite>
                  </footer>
                </blockquote>
              </div>
            </div>

            <div className="col-lg-5" data-aos="fade-left" data-aos-delay="200">
              <div className="row g-2">
                <div className="col-6">
                  <img
                    src={pilates1}
                    alt={Features.aboutPage.mainContent.clinicName[language]}
                    className="img-fluid rounded shadow-sm"
                    style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                  />
                </div>
                <div className="col-6">
                  <img
                    src={pilates2}
                    alt={Features.aboutPage.mainContent.clinicName[language]}
                    className="img-fluid rounded shadow-sm"
                    style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                  />
                </div>
                <div className="col-12">
                  <img
                    src={pilates3}
                    alt={Features.aboutPage.mainContent.clinicName[language]}
                    className="img-fluid rounded shadow-sm"
                    style={{ width: '100%', height: '220px', objectFit: 'cover' }}
                  />
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="h4 fw-bold">{Features.aboutPage.mainContent.clinicName[language]}</h3>
                <p className="text-muted fst-italic">{Features.aboutPage.mainContent.clinicTagline[language]}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="location-section section py-5" style={locationSectionStyle}>
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-5" data-aos="fade-up">
              <h2 className="fw-bold">{Features.aboutPage.location.title[language]}</h2>
              <div className="divider mx-auto my-4" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>

          <div className="row gy-4 align-items-center">
            <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
              <div className="p-4 rounded shadow-sm h-100" style={cardStyle}>
                <p>
                  {Features.aboutPage.location.description1[language]}
                </p>
                <p>
                  {Features.aboutPage.location.description2[language]}
                </p>
                <p className="fw-bold mb-0">
                  {Features.aboutPage.location.directions[language]}
                </p>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-left" data-aos-delay="200">
              <div className="map-container rounded shadow-sm overflow-hidden">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3003.3590764860673!2d-8.56701!3d41.2119!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2465e2faa85e99%3A0x5c2a7e1d6b3a0a1d!2sAv.%20Eng.%20Duarte%20Pacheco%2C%20642%2C%204445-416%20Ermesinde!5e0!3m2!1spt-PT!2spt!4v1625584000000!5m2!1spt-PT!2spt" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy"
                  title={Features.aboutPage.location.title[language]}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vitality Section */}
      <section className="vitality-section section py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-5" data-aos="fade-up">
              <h2 className="fw-bold">{Features.aboutPage.vitality.title[language]}</h2>
              <div className="divider mx-auto my-4" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-10 mx-auto" data-aos="zoom-in" data-aos-delay="100">
              <div className="vitality-card text-center p-5 rounded shadow-sm" style={cardStyle}>
                <div className="icon-box mb-4">
                  <i className="fa-solid fa-heart-pulse fa-3x text-primary"></i>
                </div>
                <h3 className="mb-4">{Features.aboutPage.vitality.subtitle[language]}</h3>
                <p className="mb-4">
                  {Features.aboutPage.vitality.description[language]}
                </p>
                <button 
                  onClick={handleAppointmentClick}
                  className="btn btn-primary rounded-pill px-4 py-2"
                >
                  <i className="bi bi-calendar-check me-2"></i>
                  {Features.aboutPage.vitality.scheduleButton[language]}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutUsPage; 